import type { SavedStrategy } from "@/types/api";
import type { StrategyLayer } from "@/types/strategy";
import type { Template } from "@/types/template";

export const SAVED_STRATEGY_TEMPLATE_ID_PREFIX = "saved:";

export function simulatorTabIdForTemplate(
  template: Pick<Template, "id">,
): string | null {
  return template.id.startsWith(SAVED_STRATEGY_TEMPLATE_ID_PREFIX)
    ? null
    : template.id;
}

export function workspaceStrategyBelongsToTemplate(
  template: Pick<Template, "id" | "saved_strategy_id">,
  strategy: {
    simulator_tab_id: string | null;
    saved_strategy_id: string | null;
  },
): boolean {
  if (template.saved_strategy_id) {
    return strategy.saved_strategy_id === template.saved_strategy_id;
  }
  return strategy.simulator_tab_id === simulatorTabIdForTemplate(template);
}

export function savedStrategyToTemplate(strategy: SavedStrategy): Template {
  return {
    id: `${SAVED_STRATEGY_TEMPLATE_ID_PREFIX}${strategy.id}`,
    saved_strategy_id: strategy.id,
    name: strategy.name,
    layers: strategy.layers as StrategyLayer[],
    scenario: { draft: false },
    notes: strategy.notes,
    tags: [...strategy.tags],
    archived_at: null,
    ticker: strategy.ticker,
    created_at: strategy.created_at,
    updated_at: strategy.updated_at,
  };
}

export function mergeStrategyLibraryTemplates({
  activeTemplates,
  archivedTemplates,
  savedStrategies,
}: {
  activeTemplates: readonly Template[];
  archivedTemplates: readonly Template[];
  savedStrategies: readonly SavedStrategy[];
}): Template[] {
  const tabTemplates = [...activeTemplates, ...archivedTemplates].filter(
    (template) => !template.scenario?.draft,
  );
  const bySavedId = new Map(
    tabTemplates
      .filter((template) => template.saved_strategy_id)
      .map((template) => [template.saved_strategy_id, template]),
  );
  const savedIds = new Set(savedStrategies.map((strategy) => strategy.id));
  const savedTemplates = savedStrategies.map((strategy) => {
    const tabTemplate = bySavedId.get(strategy.id);
    if (!tabTemplate) return savedStrategyToTemplate(strategy);
    return {
      ...tabTemplate,
      saved_strategy_id: strategy.id,
      name: strategy.name,
      notes: strategy.notes,
      tags: [...strategy.tags],
      created_at: strategy.created_at,
      updated_at: strategy.updated_at,
    };
  });
  const orphanTabs = tabTemplates.filter(
    (template) =>
      !template.saved_strategy_id || !savedIds.has(template.saved_strategy_id),
  );

  return [...savedTemplates, ...orphanTabs];
}
